import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ExperienceCard } from './ExperienceCard';
import { ArrowRight } from 'lucide-react';
import { fetchClickStats, rankByClicksWithMinLocation } from '../lib/popularExperiences';

// `experiences` is expected to be the pre-partitioned `thingsPool` from
// `splitHomepageRows` so cards never overlap with the Places-to-visit row.
// The historical category filtering is now done inside that helper.
export function WhatToDoSection({ experiences = [] }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [clickStats, setClickStats] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchClickStats().then((stats) => { if (alive) setClickStats(stats); });
    return () => { alive = false; };
  }, []);

  const activities = useMemo(() => {
    if (clickStats === null) return [];
    // `experiences` is already the pre-split Things-to-do pool: limited_event
    // is filtered out and overlap with Places has been resolved upstream.
    return rankByClicksWithMinLocation(experiences || [], clickStats, {
      count: 7,
      shuffle: true,
      cacheKey: 'things-to-do',
      location: 'sitges',
      minFromLocation: 3,
    });
  }, [experiences, clickStats]);

  if (activities.length === 0) return null;

  return (
    <section className="py-[30px] sm:py-[40px]" data-testid="what-to-do-section" id="whattodo">
      <div className="max-w-[1280px] mx-auto">
        <h2
          className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight px-5 lg:px-6"
        >
          {t('things_to_do')}
        </h2>

        <div className="overflow-x-auto pt-6 pb-6 mt-3 no-scrollbar lg:overflow-visible lg:pt-0 lg:pb-4 lg:mt-7">
          <div className="flex gap-4 snap-x snap-mandatory pl-5 pr-5 lg:grid lg:grid-cols-4 lg:gap-5 lg:snap-none lg:pl-6 lg:pr-6">
          {activities.map((exp, i) => (
            <ExperienceCard
              key={exp.id}
              exp={exp}
              testIdPrefix="activity"
              containerClassName="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0"
              animationDelay={i * 0.07}
            />
          ))}
          
          {/* Arrow card linking to Explore page */}
          <div className="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0 rounded-2xl flex flex-col items-center justify-center gap-4">
            <button
              onClick={() => navigate('/Explore')}
              className="w-20 h-20 rounded-full bg-[#3c84ff] hover:bg-[#2a6fe0] flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <ArrowRight className="w-10 h-10 text-white" strokeWidth={2.5} />
            </button>
            <span className="text-[#0F3B63] font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('view_all_activities')}
            </span>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}
